import { Color } from '@tiptap/extension-color';
import Link from '@tiptap/extension-link';
import Subscript from '@tiptap/extension-subscript';
import Superscript from '@tiptap/extension-superscript';
import TextAlign from '@tiptap/extension-text-align';
import { TextStyle } from '@tiptap/extension-text-style';
import Underline from '@tiptap/extension-underline';
import { EditorContent, useEditor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import { AlignCenter, AlignJustify, AlignLeft, AlignRight, Bold, Braces, Brackets, Droplet, FlaskConical, Italic, Subscript as SubscriptIcon, Superscript as SuperscriptIcon, Underline as UnderlineIcon } from 'lucide-react';
import { lazy, Suspense, useEffect, useReducer, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';

import { AnswerRefChip } from './answer-ref-chip-extension';
import { aliasChipName, answerRefAddress, useAnswerRefScope } from './answer-ref-context';
import { AnswerRefPickerMenu } from './answer-ref-picker-menu';
import { BlankChip } from './blank-chip-extension';
import { INK_COLORS, remapInkColors } from './ink-colors';
import { InkSwatchGrid } from './ink-swatch-grid';
import { MathFormula, processMathHTML } from './math-formula-extension';
import { ReactionFormula, processReactionHTML } from './reaction-formula-extension';
import { useIsPhone } from './use-viewport';
import { VariableChip } from './variable-chip-extension';

import type { PickerHandle } from '@/components/task/flow-picker-menu';
import type { Editor } from '@tiptap/core';


import { useRecallAffordance } from '@/components/task/use-recall-affordance';
import { useVariableAffordance } from '@/components/task/use-variable-affordance';
import { VariablePickerMenu } from '@/components/task/variable-picker-menu';
import { ToolbarButton } from '@/components/ui/icon-button';
import { useTaskVariables } from '@/contexts/task-variables-context';

const MathEditorModal = lazy(() =>
  import('./math-editor-modal').then(m => ({ default: m.MathEditorModal }))
);

const ReactionsEditorModal = lazy(() =>
  import('./reactions-editor-modal').then(m => ({ default: m.ReactionsEditorModal }))
);


const ALIGNS = ['left', 'center', 'right', 'justify'] as const;

type SymbolItem = { sym: string; labelKey: string; isCommon?: boolean };
// No dedicated "Common" section — it was a pure duplicate of entries already present in their own
// proper section below (every symbol in it reappeared verbatim in Greek/Math/Geometry), so it cost
// a full header+row of space in an already-cramped popup without adding any symbol that wasn't
// reachable elsewhere. `isCommon` instead highlights those same entries in place (see the button's
// own render), preserving quick-scan discoverability without the duplicate section.
export const SYMBOL_SECTIONS: { key: string; items: SymbolItem[] }[] = [
  { key: 'greek', items: [
    { sym: 'α', labelKey: 'blocks.symbols.alpha', isCommon: true },   { sym: 'β', labelKey: 'blocks.symbols.beta', isCommon: true },    { sym: 'γ', labelKey: 'blocks.symbols.gamma' },
    { sym: 'δ', labelKey: 'blocks.symbols.delta', isCommon: true },   { sym: 'ε', labelKey: 'blocks.symbols.epsilon' }, { sym: 'ζ', labelKey: 'blocks.symbols.zeta' },
    { sym: 'η', labelKey: 'blocks.symbols.eta' },     { sym: 'θ', labelKey: 'blocks.symbols.theta', isCommon: true },   { sym: 'ι', labelKey: 'blocks.symbols.iota' },
    { sym: 'κ', labelKey: 'blocks.symbols.kappa' },   { sym: 'λ', labelKey: 'blocks.symbols.lambda', isCommon: true },  { sym: 'μ', labelKey: 'blocks.symbols.mu', isCommon: true },
    { sym: 'ν', labelKey: 'blocks.symbols.nu' },      { sym: 'ξ', labelKey: 'blocks.symbols.xi' },      { sym: 'π', labelKey: 'blocks.symbols.pi', isCommon: true },
    { sym: 'ρ', labelKey: 'blocks.symbols.rho' },     { sym: 'σ', labelKey: 'blocks.symbols.sigma', isCommon: true },   { sym: 'τ', labelKey: 'blocks.symbols.tau' },
    { sym: 'υ', labelKey: 'blocks.symbols.upsilon' }, { sym: 'φ', labelKey: 'blocks.symbols.phi' },     { sym: 'χ', labelKey: 'blocks.symbols.chi' },
    { sym: 'ψ', labelKey: 'blocks.symbols.psi' },     { sym: 'ω', labelKey: 'blocks.symbols.omega', isCommon: true },
    { sym: 'Γ', labelKey: 'blocks.symbols.gammaUpper' },   { sym: 'Δ', labelKey: 'blocks.symbols.deltaUpper', isCommon: true },
    { sym: 'Θ', labelKey: 'blocks.symbols.thetaUpper' },   { sym: 'Λ', labelKey: 'blocks.symbols.lambdaUpper' },
    { sym: 'Ξ', labelKey: 'blocks.symbols.xiUpper' },      { sym: 'Π', labelKey: 'blocks.symbols.piUpper' },
    { sym: 'Σ', labelKey: 'blocks.symbols.sigmaUpper' },   { sym: 'Υ', labelKey: 'blocks.symbols.upsilonUpper' },
    { sym: 'Φ', labelKey: 'blocks.symbols.phiUpper' },     { sym: 'Ψ', labelKey: 'blocks.symbols.psiUpper' },
    { sym: 'Ω', labelKey: 'blocks.symbols.omegaUpper' },
  ]},
  { key: 'math', items: [
    { sym: '∞', labelKey: 'blocks.symbols.infinity', isCommon: true },       { sym: '±', labelKey: 'blocks.symbols.plusMinus', isCommon: true },
    { sym: '∓', labelKey: 'blocks.symbols.minusPlus' },      { sym: '×', labelKey: 'blocks.symbols.multiplication', isCommon: true },
    { sym: '÷', labelKey: 'blocks.symbols.division' },        { sym: '≠', labelKey: 'blocks.symbols.notEqual' },
    { sym: '≤', labelKey: 'blocks.symbols.lessEqual' },       { sym: '≥', labelKey: 'blocks.symbols.greaterEqual' },
    { sym: '≈', labelKey: 'blocks.symbols.approximately', isCommon: true },   { sym: '≡', labelKey: 'blocks.symbols.identical' },
    { sym: '∝', labelKey: 'blocks.symbols.proportional' },    { sym: '√', labelKey: 'blocks.symbols.squareRoot' },
    { sym: '∑', labelKey: 'blocks.symbols.summation' },       { sym: '∫', labelKey: 'blocks.symbols.integral' },
    { sym: '∮', labelKey: 'blocks.symbols.contourIntegral' }, { sym: '∈', labelKey: 'blocks.symbols.elementOf' },
    { sym: '∉', labelKey: 'blocks.symbols.notElementOf' },    { sym: '∩', labelKey: 'blocks.symbols.intersection' },
    { sym: '∪', labelKey: 'blocks.symbols.union' },           { sym: '∀', labelKey: 'blocks.symbols.forAll' },
    { sym: '∃', labelKey: 'blocks.symbols.thereExists' },
    { sym: '→', labelKey: 'blocks.symbols.rightArrow' },      { sym: '←', labelKey: 'blocks.symbols.leftArrow' },
    { sym: '↔', labelKey: 'blocks.symbols.biArrow' },         { sym: '⇒', labelKey: 'blocks.symbols.impliesArrow' },
    { sym: '⇔', labelKey: 'blocks.symbols.iffArrow' },        { sym: '⇌', labelKey: 'blocks.symbols.equilibriumArrow' },
    /* Prose only, and deliberately not in the equation editor: MathLive turns a typed `'` into a
       prime by itself, while here it stays an apostrophe and there is no other way in. */
    { sym: '′', labelKey: 'blocks.symbols.prime' },           { sym: '″', labelKey: 'blocks.symbols.doublePrime' },
    { sym: 'ℕ', labelKey: 'blocks.symbols.naturals' },        { sym: 'ℤ', labelKey: 'blocks.symbols.integers' },
    { sym: 'ℚ', labelKey: 'blocks.symbols.rationals' },       { sym: 'ℝ', labelKey: 'blocks.symbols.reals' },
    { sym: 'ℂ', labelKey: 'blocks.symbols.complexNums' },
  ]},
  { key: 'geometry', items: [
    { sym: '°', labelKey: 'blocks.symbols.degree', isCommon: true },        { sym: '∠', labelKey: 'blocks.symbols.angle' },
    { sym: '∡', labelKey: 'blocks.symbols.measuredAngle' }, { sym: '∟', labelKey: 'blocks.symbols.rightAngle' },
    { sym: '△', labelKey: 'blocks.symbols.triangle' },      { sym: '⊥', labelKey: 'blocks.symbols.perpendicular' },
    { sym: '∥', labelKey: 'blocks.symbols.parallel' },      { sym: '⌒', labelKey: 'blocks.symbols.arc' },
  ]},
  { key: 'physics', items: [
    { sym: 'ℏ', labelKey: 'blocks.symbols.hBar' },         { sym: 'ℓ', labelKey: 'blocks.symbols.scriptEll' },
    { sym: 'Å', labelKey: 'blocks.symbols.angstrom' },      { sym: '∂', labelKey: 'blocks.symbols.partial' },
    { sym: '∇', labelKey: 'blocks.symbols.nabla' },         { sym: 'ℜ', labelKey: 'blocks.symbols.realPart' },
    { sym: 'ℑ', labelKey: 'blocks.symbols.imagPart' },      { sym: 'ℰ', labelKey: 'blocks.symbols.scriptE' },
    { sym: 'ℬ', labelKey: 'blocks.symbols.scriptB' },       { sym: 'ℋ', labelKey: 'blocks.symbols.scriptH' },
    { sym: 'ℒ', labelKey: 'blocks.symbols.lagrangian' },    { sym: 'ℱ', labelKey: 'blocks.symbols.fourierTransform' },
  ]},
  /*
   * The Sun, the Moon, a star, and the planets in orbital order.
   *
   * `♀` and `♂` carry U+FE0E, the text variation selector, because they are the two glyphs here
   * that Unicode also lists as emoji: without it a platform is free to draw Venus as a pink
   * cartoon. The rest have no emoji form, so a selector on them would be a stray codepoint in
   * the teacher's text for nothing.
   *
   * Both are named for both readings. The same glyph is Venus to an astronomer and the female
   * sign to a biologist, and a teacher looking for the second should still find it.
   */
  /*
   * Organic chemistry as it appears in a SENTENCE — "if you look at H–C=C–H, you would…".
   *
   * The reaction editor has had the bonds since its own organic round; prose had neither the
   * bond dash nor `=`, so a condensed formula typed into a sentence came out with hyphens where
   * it wanted bonds. `≡` appears here as well as under Math: the same glyph is the triple bond
   * to a chemist and "identical to" to a mathematician, and whichever name you scan for should
   * find it.
   *
   * `⌬` has patchy font coverage outside macOS, so it is the one entry here that needs
   * checking on Windows and Android before anyone relies on it.
   */
  { key: 'organic', items: [
    { sym: '\u2013', labelKey: 'blocks.symbols.singleBond' },  { sym: '=', labelKey: 'blocks.symbols.doubleBondChem' },
    { sym: '\u2261', labelKey: 'blocks.symbols.tripleBond' },  { sym: '\u232c', labelKey: 'blocks.symbols.benzeneRing' },
    { sym: '\u00b7', labelKey: 'blocks.symbols.radicalDot' },  { sym: '\u2021', labelKey: 'blocks.symbols.transitionState' },
    { sym: '\u21b7', labelKey: 'blocks.symbols.curlyArrowRight' }, { sym: '\u21b6', labelKey: 'blocks.symbols.curlyArrowLeft' },
  ]},
  { key: 'astronomy', items: [
    { sym: '☉', labelKey: 'blocks.symbols.sun' },          { sym: '☽', labelKey: 'blocks.symbols.moon' },
    { sym: '⋆', labelKey: 'blocks.symbols.star' },         { sym: '☿', labelKey: 'blocks.symbols.mercury' },
    { sym: '♀\uFE0E', labelKey: 'blocks.symbols.venus' },  { sym: '⊕', labelKey: 'blocks.symbols.earth' },
    { sym: '♂\uFE0E', labelKey: 'blocks.symbols.mars' },   { sym: '♃', labelKey: 'blocks.symbols.jupiter' },
    { sym: '♄', labelKey: 'blocks.symbols.saturn' },       { sym: '♅', labelKey: 'blocks.symbols.uranus' },
    { sym: '♆', labelKey: 'blocks.symbols.neptune' },      { sym: '♇', labelKey: 'blocks.symbols.pluto' },
  ]},
];
/**
 * Every equation, or every reaction, already in this field — what an insert editor's Common
 * band is derived from.
 *
 * Scoped to the document the editor was opened from rather than the whole task: it is what is
 * reachable without new plumbing, and it is the honest reading of "this document". A task whose
 * equations are spread over several blocks will adapt per block, which is a narrower window than
 * the design imagined but never a wrong one — every equation it counts is one the teacher wrote
 * right here.
 *
 * Both inserts are read the same way because both bands work the same way; they differ only in
 * which node holds the source and what the attribute is called.
 */
function documentSourcesOf(ed: Editor | null, nodeType: string, attr: string): string[] {
  if (!ed) return [];
  const found: string[] = [];
  ed.state.doc.descendants((node) => {
    if (node.type.name === nodeType && typeof node.attrs[attr] === 'string') {
      found.push(node.attrs[attr]);
    }
  });
  return found;
}

const documentLatexOf = (ed: Editor | null) => documentSourcesOf(ed, 'mathFormula', 'latex');
const documentFormulasOf = (ed: Editor | null) => documentSourcesOf(ed, 'reactionFormula', 'formula');

const TOTAL_SYMBOLS = SYMBOL_SECTIONS.reduce((n, s) => n + s.items.length, 0);
const SYMBOLS_COLS = 8;

// Popup pixel widths (must match each popup's own w-* Tailwind class) — needed by
// popupHorizontalAnchor's clamp math, which can't read a Tailwind class back out of the DOM
// before the popup has actually rendered once.
const COLOR_POPUP_WIDTH_PX = 224; // w-56
const SYMBOLS_POPUP_WIDTH_PX = 384; // w-96


function getAlignEntry(idx: number) {
  if (idx === 1) return { align: 'center'  as const, Icon: AlignCenter,  labelKey: 'blocks.toolbar.alignCenter' };
  if (idx === 2) return { align: 'right'   as const, Icon: AlignRight,   labelKey: 'blocks.toolbar.alignRight'  };
  if (idx === 3) return { align: 'justify' as const, Icon: AlignJustify, labelKey: 'blocks.toolbar.justify'     };
  return         { align: 'left'    as const, Icon: AlignLeft,    labelKey: 'blocks.toolbar.alignLeft'   };
}

interface RichTextEditorProps {
  content: string;
  onChange: (html: string) => void;
  /** Names the data key or item this editor writes, so an undo can point at it. See
   * `field-reveal.ts` — the id an undo looks for is the one `changedFields` derives from the data,
   * which for a rich-text surface is either the key (`question`) or the id of the item it edits. */
  fieldId?: string;
  placeholder?: string;
  autoFocus?: boolean;
  editorClassName?: string;
  /**
   * Make the editor FILL its parent, with the toolbar pinned and the text area scrolling on its own.
   *
   * Opt-in because most callers use this for a question, where sizing to content is right. Without
   * it the root is auto-height, so a `flex-1 overflow-y-auto` on the text area has no bounded parent
   * to fill: it grows instead of scrolling, and whatever clips it makes the overflow unreachable.
   * That is the height chain, and it breaks in the middle if this element does not take part.
   *
   * `'md'` fills only from the `md` breakpoint up, for a caller whose container is itself only
   * height-bounded there and grows with its content below it.
   */
  fill?: boolean | 'md';
  /** Suppress the toolbar and modals — renders only the editor content area */
  hideToolbar?: boolean;
  /** Suppress the editor content area — renders only the toolbar (use with targetEditor) */
  hideContent?: boolean;
  /** CSS-hide the toolbar without changing DOM structure (preserves focus across transitions) */
  toolbarVisible?: boolean;
  /** 'vertical' stacks both button groups into a narrow rail (phone-landscape 3-column layout)
   * instead of the default horizontal rows, and reflows the 3 popup panels (color/symbols/
   * variables) to open sideways with an explicit width instead of below at the trigger's own
   * (in this mode, too narrow) column width. Defaults to 'horizontal' — every existing caller is
   * unaffected. */
  toolbarLayout?: 'horizontal' | 'vertical';
  /** Whether this instance is rendering inside the phone full-screen editor dialog, in EITHER
   * orientation — `toolbarLayout` alone can't tell phone-portrait apart from desktop/tablet, since
   * both use `'horizontal'` (only the phone-landscape rail uses `'vertical'`). The 3 popups
   * (color/symbols/variable) portal to document.body with a flip+clamp position when this is true
   * OR `toolbarLayout` is `'vertical'` — needed on phone (confirmed live: other page UI could
   * visually cover a plain in-place popup there) — but NOT on desktop/tablet, where the original
   * plain `absolute` popup already worked fine and a portaled, independently-positioned popup
   * instead started overlapping unrelated neighboring toolbar buttons it was never meant to cover.
   * Defaults to false — every existing (desktop/tablet) caller is unaffected. */
  phoneFullScreen?: boolean;
  /** Called once when the TipTap Editor instance is ready */
  onEditorReady?: (editor: Editor) => void;
  /** Called when this editor receives focus — use this to track shared-toolbar active editor */
  onEditorFocus?: () => void;
  /** When set, all toolbar commands target this editor instead of the local one */
  targetEditor?: Editor | null;
}

/** All 3 toolbar popups (color/symbols/variable) portal to document.body with `position: fixed`
 * now, in a phone context (both toolbar layouts) — not just the vertical rail. Confirmed live: the
 * old horizontal (portrait) popups stayed plain `absolute`-positioned in-place, which let other
 * page UI (the warning/error bar) visually cover their bottom portion — a stacking-context issue
 * no local z-index can escape, the same class of problem `FormulaField`'s picker and the Options
 * flyout already solve this exact way elsewhere in this codebase.
 *
 * Anchored with an explicit `gap` from the trigger's own edge (not flush against it) — confirmed
 * live this is load-bearing for the portrait toolbar case: `left` there aligns with the trigger's
 * own left edge (see popupHorizontalAnchor), so the popup sits directly over the same horizontal
 * span as the button. Without a vertical gap too, the popup's near edge lands exactly on the
 * trigger's own edge and visually swallows the button that opened it.
 *
 * The available height on the CHOSEN side becomes this popup's own `maxHeightPx`, capped at
 * `desiredMaxHeight` — NOT a shared static ceiling clamped separately from position. An earlier
 * version tried clamping `top`/`bottom` directly against a fixed ceiling (`min(700px, dvh-120px)`)
 * while leaving the popup's own max-height at that same static value; confirmed live this was
 * wrong whenever the actual space on the chosen side was smaller than the ceiling — the position
 * clamp kept the popup on-screen by sliding it toward the trigger, but its height stayed at the
 * (too-generous) static ceiling, so it silently overlapped the trigger it was supposed to leave
 * clear. Computing `maxHeightPx` from the real, measured space instead makes both guarantees hold
 * at once: never off-screen (height physically can't exceed available space) and never overlapping
 * the trigger (position is always exactly `gap` away from it, never clamped toward it). This also
 * means the popup automatically uses however much room is genuinely free — capped only by
 * `desiredMaxHeight`, not a guessed buffer — so it's automatically as tall as it can safely be.
 *
 * Horizontal positioning (`left`) is computed separately per call site below — vertical rail and
 * portrait toolbar want different anchoring on that axis (opening beside a narrow rail vs. staying
 * near a wide toolbar row), so it isn't shared here. */
function popupVerticalAnchor(rect: DOMRect, desiredMaxHeight: number): { top: number; maxHeightPx: number } | { bottom: number; maxHeightPx: number } {
  const margin = 8;
  const gap = 8;
  const spaceBelow = window.innerHeight - rect.bottom - gap - margin;
  const spaceAbove = rect.top - gap - margin;
  if (spaceBelow >= spaceAbove) {
    return { top: rect.bottom + gap, maxHeightPx: Math.min(desiredMaxHeight, spaceBelow) };
  }
  return { bottom: window.innerHeight - rect.top + gap, maxHeightPx: Math.min(desiredMaxHeight, spaceAbove) };
}

/** Horizontal-axis anchor — clamps a preferred left position so a fixed-width popup can never
 * render off either side of the viewport. The preferred position itself differs by caller: the
 * vertical rail opens beside its (narrow) trigger column (`rect.right + 8`), while the portrait
 * toolbar opens aligned with the trigger's own left edge (`rect.left`) since there's no separate
 * "column" to open beside — a wide toolbar row IS the content. */
function popupHorizontalAnchor(idealLeft: number, popupWidthPx: number): number {
  const margin = 8;
  return Math.max(margin, Math.min(idealLeft, window.innerWidth - popupWidthPx - margin));
}

export function RichTextEditor({ content, onChange, fieldId, placeholder, autoFocus, editorClassName, fill, hideToolbar, hideContent, toolbarVisible, toolbarLayout = 'horizontal', phoneFullScreen = false, onEditorReady, onEditorFocus, targetEditor }: RichTextEditorProps) {
  /**
   * Shrink the ProseMirror child -- ONLY where the editor is filling its container.
   *
   * `[&>.tiptap]:min-h-0` compiles to a two-class descendant selector, so it outranks the plain
   * `min-h-20` / `min-h-[60px]` / `min-h-6` that nine call sites pass as `editorClassName`. It was
   * unconditional, so it silently cancelled every one of them: the field collapsed to its content
   * and the stated minimum did nothing anywhere in the app. Visible only once something needed the
   * reserved room -- FITB's two-line placeholder, which then hung 6px out of a 46px box.
   *
   * It is still required when `fill` is set: a flex child refuses to shrink below its content, so
   * a `flex-1 overflow-y-auto` ProseMirror grew to its full text height instead of scrolling and
   * the clip above it put the last lines out of reach. `text-block` is the only caller that fills,
   * and `'md'` fills only from that breakpoint up -- so the override follows the same shape the
   * wrapper below already uses.
   */
  const tiptapFill = fill === 'md'
    ? 'md:[&>.tiptap]:min-h-0 md:[&>.tiptap]:flex-1'
    : fill ? '[&>.tiptap]:min-h-0 [&>.tiptap]:flex-1' : '';

  const { t } = useTranslation();
  const variables = useTaskVariables();
  /*
   * MAY THIS BLOCK TAKE A VARIABLE — which is not the same question as whether any exist.
   * `variables` above is for RESOLUTION and is never narrowed; this decides the affordances.
   *
   * GRANDFATHERED per field, the same as the formula field and the DataViz cell. Prose that
   * already holds a chip — pasted in, or left behind when a rung was lowered — keeps its button
   * live, so a teacher can put the same variable in a second sentence instead of being able to
   * read one on screen and not write it. The three insert surfaces of one block disagreeing
   * about whether variables exist was the inconsistency this closes.
   *
   * The prop rather than the live document, deliberately: deleting the last chip mid-edit should
   * not pull the button out from under the caret.
   */
  const varAffordance = useVariableAffordance();
  /* The recall half of the same question, with the same three states — see
     `useRecallAffordance` for why it answers the DESTINATION only. */
  const refAffordance = useRecallAffordance();
  // Phone (either orientation) gets the portaled/flip/clamp popup treatment; desktop/tablet keeps
  // the original plain in-place popup — see phoneFullScreen's own doc comment for why toolbarLayout
  // alone can't make this distinction.
  const isPhoneContext = toolbarLayout === 'vertical' || phoneFullScreen;
  const isPhone = useIsPhone();
  // Distinguishes the 2 phone contexts from each other — the color popup wants full-width in
  // portrait (matching desktop's own w-full) but a fixed, rail-appropriate width in landscape.
  const isPhonePortrait = phoneFullScreen && toolbarLayout !== 'vertical';
  // Landscape's rail is narrow but the screen itself is wide and short — a tall, narrow 8-column
  // grid (portrait's shape) needs far more vertical room than that viewport has to spare. Reflowing
  // to many more, wider columns trades unused width for fewer rows, cutting how much (if any)
  // scrolling the symbols popup needs there. Portrait keeps its original 8-column/w-96 shape,
  // which already had enough width to spare without needing this.
  // 17 columns — enough for the largest section (Greek, 33 letters) to fit in exactly 2 rows
  // (ceil(33/17) = 2) instead of 3, per the user's own ask. Width sized to match: 17 * 40px
  // buttons + 16 * 4px gaps + 24px padding ≈ 768px minimum, rounded up a bit for breathing room.
  const symbolsCols = toolbarLayout === 'vertical' ? 17 : SYMBOLS_COLS;
  const symbolsPopupWidthPx = toolbarLayout === 'vertical' ? 780 : SYMBOLS_POPUP_WIDTH_PX;
  const [colorPopup, setColorPopup] = useState(false);
  const [lastColor, setLastColor] = useState<string | null>(null);
  const [colorLiveMsg, setColorLiveMsg] = useState('');
  const colorWrapperRef = useRef<HTMLDivElement>(null);
  const [symbolsPopup,   setSymbolsPopup]   = useState(false);
  const [symbolFocusIdx, setSymbolFocusIdx] = useState(0);
  const symbolsWrapperRef = useRef<HTMLDivElement>(null);

  // Both toolbarLayouts portal these 3 popups to document.body instead of rendering in-place.
  // Vertical rail: every ancestor column in that layout is its own overflow-y-auto scroller, and
  // setting overflow on only one axis forces the browser to clip the other too (no way to allow
  // horizontal overflow while clipping vertically on the same element), so a plain `absolute`
  // popup gets silently clipped the moment it extends past its narrow rail. Horizontal/portrait:
  // confirmed live a plain in-place `absolute` popup could be visually covered by other page UI
  // (the warning/error bar) — a stacking-context issue no local z-index escapes either. Position
  // computed from the trigger, mirroring FormulaField's identical escape-hatch for the same
  // reason. Each popup needs its own panel ref (distinct from its wrapper ref, which only
  // contains the trigger button once portaled) so the click-outside handler still recognizes
  // clicks inside the portaled panel.
  type PopupAnchor = { top: number; left: number; maxHeightPx: number } | { bottom: number; left: number; maxHeightPx: number };
  // Ceiling passed to popupVerticalAnchor — the popup will use however much of this is genuinely
  // free on whichever side it opens, never more (see that function's own doc comment).
  const POPUP_DESIRED_MAX_HEIGHT = 700;
  const [colorPopupPos, setColorPopupPos] = useState<PopupAnchor | null>(null);
  const colorPopupPanelRef = useRef<HTMLDivElement>(null);
  const [symbolsPopupPos, setSymbolsPopupPos] = useState<PopupAnchor | null>(null);
  const symbolsPopupPanelRef = useRef<HTMLDivElement>(null);
  const [mathModal, setMathModal] = useState(false);
  const [mathInitialLatex, setMathInitialLatex] = useState('');
  const mathUpdateRef = useRef<((latex: string) => void) | null>(null);
  const [reactionsModal, setReactionsModal] = useState(false);
  const [reactionsInitialFormula, setReactionsInitialFormula] = useState('');
  const [reactionsInitialNote, setReactionsInitialNote] = useState('');
  /* The mediator doubles as the Feynman flag: empty means draw the equation as an equation. */
  const [reactionsInitialFeynman, setReactionsInitialFeynman] = useState('');
  const reactionsUpdateRef = useRef<((next: { formula: string; note: string; feynman: string }) => void) | null>(null);
  const prevContentRef = useRef(content);
  const [, forceUpdate] = useReducer((x: number) => x + 1, 0);

  /**
   * `{` OPENS THE VARIABLE PICKER, at the caret.
   *
   * The braces ARE the notation everywhere else — chips read `{name}`, formula fields take
   * `{name}`, the variables panel shows `{name}` — so typing one to get one is not a new idea
   * to learn, and what appears is visually continuous with what was pressed. `@` was the other
   * candidate and means *person* to anyone who has used Slack.
   *
   * The brace is SWALLOWED rather than inserted and later removed: the menu takes focus into
   * its own filter, so the characters after it are the search rather than the document, and
   * there is no position to keep track of. Escape puts the brace back, which is the whole
   * escape hatch for the one case this costs — set notation like `{1, 2, 3}` now needs a
   * dismiss first.
   */
  const variablePickerRef = useRef<PickerHandle | null>(null);
  const answerRefPickerRef = useRef<PickerHandle | null>(null);
  const answerRefScope = useAnswerRefScope();
  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: false,
        blockquote: false,
        code: false,
        codeBlock: false,
        horizontalRule: false,
      }),
      Underline,
      TextStyle,
      Color,
      Subscript,
      Superscript,
      TextAlign.configure({ types: ['paragraph'] }),
      Link.configure({
        openOnClick: false,
        HTMLAttributes: { target: '_blank', rel: 'noopener noreferrer' },
      }),
      MathFormula.configure({
        onEditRequest: (latex, update) => {
          mathUpdateRef.current = update;
          setMathInitialLatex(latex);
          setMathModal(true);
        },
      }),
      ReactionFormula.configure({
        onEditRequest: (formula, note, feynman, update) => {
          reactionsUpdateRef.current = update;
          setReactionsInitialFormula(formula);
          setReactionsInitialNote(note);
          setReactionsInitialFeynman(feynman);
          setReactionsModal(true);
        },
      }),
      VariableChip,
      AnswerRefChip.configure({
        // Same shape as the two formula extensions above: the node view hands up a value and a
        // commit, and the dialog lives out here where focus is not contenteditable's business.
      }),
      BlankChip,
    ],
    // Remapped on the way IN as well as out: content stored before the theme tokens existed holds
    // a literal colour, and without this the EDITOR would show it fixed while every display surface
    // showed it inverting — the inconsistency a teacher notices first. Upgrading here also means
    // the next save persists the token, so each block heals as it is opened.
    content: remapInkColors(content),
    autofocus: autoFocus ? 'end' : false,
    editorProps: {
      handleKeyDown: (view, event) => {
        /*
         * NOT ON A PHONE, for either bracket.
         *
         * The shortcut is keyed off a keystroke, and a soft keyboard sends one too — so typing
         * a literal `[` on a phone summoned a full-width menu over a field a teacher was in
         * the middle of. Worse, Escape is what hands the character back and a soft keyboard
         * has no Escape, so the only way out also loses the bracket. Chemistry types `[` on
         * purpose, constantly.
         *
         * `isPhone` and not `pointer: coarse`: an iPad with a keyboard is coarse and is
         * exactly where a teacher WOULD reach for this, so the test has to be the small
         * screen rather than the touch screen. The toolbar button is the path on a phone.
         */
        if (isPhone) return false;
        /*
         * `[` opens the earlier-answer picker, exactly as `{` opens the variable one below.
         *
         * Reaching for the toolbar is the slow path; the bracket is the signifier on the
         * button, so typing it is the thing a teacher will try. The same two guards apply —
         * nothing to offer and no mounted picker both let the bracket through, because a
         * shortcut that eats your keystroke and shows nothing is worse than no shortcut.
         */
        if (event.key === '[' && !event.metaKey && !event.ctrlKey && !event.altKey) {
          if (!answerRefScope || !answerRefPickerRef.current) return false;
          /* A typed bracket is an insertion, so it answers to the same gate the button does,
             and falls through as a literal `[` — which is what someone typing one wanted. */
          if (refAffordance !== 'allowed') return false;
          const at = view.coordsAtPos(view.state.selection.from);
          answerRefPickerRef.current.openAt(
            new DOMRect(at.left, at.top, 0, Math.max(1, at.bottom - at.top)));
          return true;
        }
        if (event.key !== '{' || event.metaKey || event.ctrlKey || event.altKey) return false;
        // Nothing to offer is not a shortcut, it is a dead key that ate your brace.
        if (!variables.some(v => v.name)) return false;
        // A typed brace is an insertion, so it answers to the same gate the button does — and
        // falls through as a literal `{`, which is what someone typing one in prose wanted.
        if (varAffordance !== 'allowed') return false;
        // Nor is a picker that is not mounted. Both branches below render one now, so this is a
        // floor rather than a live case: whatever goes wrong with the menu, the brace is typed.
        if (!variablePickerRef.current) return false;
        const at = view.coordsAtPos(view.state.selection.from);
        variablePickerRef.current?.openAt(
          new DOMRect(at.left, at.top, 0, Math.max(1, at.bottom - at.top)));
        return true;
      },
      attributes: {
        // Tiptap owns this element, so the audit hook has to be handed to it here rather than
        // written on JSX. Without it `richtext` was a token and a CSS rule with nothing to match.
        'data-field-kind': 'richtext',
        'aria-multiline': 'true',
        'aria-label': placeholder ?? t('blocks.text.editAriaEmpty'),
        class: [
          'ws-block-editor w-full min-w-0 break-words rounded border border-ws-border bg-ws-btn',
          'px-3 py-3 text-sm text-ws-text outline-none touch-manipulation',
          '[&_strong]:font-bold [&_b]:font-bold [&_em]:italic [&_i]:italic',
          '[&_u]:underline [&_s]:line-through',
          '[&_a]:text-ws-text-link [&_a]:underline',
          '[&_ul]:list-disc [&_ul]:pl-5 [&_ol]:list-decimal [&_ol]:pl-5',
          editorClassName ?? 'min-h-[200px] md:min-h-[120px]',
        ].join(' '),
      },
    },
    onFocus: () => { onEditorFocus?.(); },
    onUpdate: ({ editor }) => {
      const html = processMathHTML(processReactionHTML(editor.getHTML()));
      prevContentRef.current = html;
      onChange(html);
    },
  });

  // Sync external content changes (undo/redo from task-level history)
  useEffect(() => {
    if (!editor || content === prevContentRef.current) return;
    prevContentRef.current = content;
    editor.commands.setContent(remapInkColors(content), { emitUpdate: false });
  }, [content, editor]);

  // Re-render when local editor state changes (cursor move, mark toggle, selection)
  useEffect(() => {
    if (!editor) return;
    editor.on('transaction', forceUpdate);
    return () => { editor.off('transaction', forceUpdate); };
  }, [editor]);

  // Re-render when targetEditor's formatting state changes (bold active, alignment, etc.)
  useEffect(() => {
    if (!targetEditor) return;
    targetEditor.on('transaction', forceUpdate);
    return () => { targetEditor.off('transaction', forceUpdate); };
  }, [targetEditor]);

  // Notify parent once this editor instance is ready
  useEffect(() => {
    if (editor) onEditorReady?.(editor);
  }, [editor]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!colorPopup) { setColorPopupPos(null); return; }
    if (isPhoneContext) {
      const rect = colorWrapperRef.current?.getBoundingClientRect();
      if (rect) {
        if (isPhonePortrait) {
          // Full-width like desktop/tablet's own w-full — pinned to the screen's own edges
          // (matching the CSS w-[calc(100vw-16px)] below) rather than tied to the trigger's own
          // left edge, since "full width" means edge-to-edge regardless of where Color happens
          // to sit in the toolbar.
          setColorPopupPos({ ...popupVerticalAnchor(rect, POPUP_DESIRED_MAX_HEIGHT), left: 8 });
        } else {
          // Landscape (the only other case isPhoneContext can be true for): same trade as the
          // symbols popup — Color's trigger sits partway down the rail, so staying strictly
          // adjacent to it left only ~180px of the ~390px screen (confirmed live: a real
          // scrollbar appeared despite there being plenty of room a bit higher up). Decoupled
          // from the trigger's own position, pinned near the top, using nearly the full screen
          // height instead — this eliminates the scroll entirely (the swatch grid comfortably
          // fits well under half of even a capped height).
          const margin = 8;
          setColorPopupPos({ top: margin, maxHeightPx: window.innerHeight - margin * 2, left: popupHorizontalAnchor(rect.right + 8, COLOR_POPUP_WIDTH_PX) });
        }
      }
    }
    // Desktop/tablet: the dialog below renders inline (not portaled), so it's already in the DOM
    // — inside colorWrapperRef — by the time this same effect runs; safe to focus immediately.
    // Phone context is handled by the dedicated effect below instead (see its own comment).
    if (!isPhoneContext) {
      requestAnimationFrame(() =>
        colorWrapperRef.current?.querySelector<HTMLButtonElement>('[data-color-btn]')?.focus()
      );
    }
    function onClickOutside(e: MouseEvent) {
      if (colorWrapperRef.current && !colorWrapperRef.current.contains(e.target as Node) &&
          colorPopupPanelRef.current && !colorPopupPanelRef.current.contains(e.target as Node)) {
        setColorPopup(false);
      }
    }
    // Document-level Escape backstop, alongside the dialog's own local onKeyDown (which only
    // fires while focus is actually inside it) — this popup's content is fixed (always at least
    // the "Default" reset button), so autofocus-on-open should normally put focus there and the
    // local handler alone would suffice, but a document-level listener costs nothing and matches
    // the click-outside handler's own "regardless of focus" robustness one line above.
    function onKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        setColorPopup(false);
        requestAnimationFrame(() => colorWrapperRef.current?.querySelector<HTMLButtonElement>('button')?.focus());
      }
    }
    document.addEventListener('mousedown', onClickOutside);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onClickOutside);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [colorPopup, toolbarLayout, isPhoneContext, isPhonePortrait]);

  // Phone-context focus-on-open — split out from the effect above rather than done inline there,
  // because in a phone context the dialog below is portaled (`createPortal(dialog, document.body)`)
  // and conditioned on `colorPopupPos`, which that same effect only just *scheduled* via
  // setColorPopupPos — a plain state setter doesn't apply synchronously, so on the very render
  // that flips colorPopup to true, colorPopupPos is still null and the portal (and its panel ref)
  // don't exist yet. Confirmed live: a requestAnimationFrame queued from that same effect lost the
  // race against React's own re-render often enough that focus silently never reached the popup at
  // all. Depending on colorPopupPos itself instead guarantees this effect only runs once that
  // state has actually landed and the portal has committed with colorPopupPanelRef attached.
  useEffect(() => {
    if (!colorPopup || !isPhoneContext || !colorPopupPos) return;
    requestAnimationFrame(() =>
      colorPopupPanelRef.current?.querySelector<HTMLButtonElement>('[data-color-btn]')?.focus()
    );
  }, [colorPopup, isPhoneContext, colorPopupPos]);

  useEffect(() => {
    if (!symbolsPopup) { setSymbolsPopupPos(null); return; }
    setSymbolFocusIdx(0);
    // Portals + fixed-positions only in a phone context (either toolbarLayout) — confirmed live
    // that a plain in-place popup could be visually covered by other page UI further down (the
    // warning/error bar) in the phone full-screen dialog specifically; portaling to document.body
    // with a high z-index is the same escape hatch already used elsewhere in this file for that.
    // Desktop/tablet keeps the original plain `absolute` popup (see phoneFullScreen's own doc
    // comment) — a portaled, independently-positioned popup there started overlapping unrelated
    // neighboring toolbar buttons instead, confirmed live.
    if (isPhoneContext) {
      const rect = symbolsWrapperRef.current?.getBoundingClientRect();
      if (rect) {
        const idealLeft = toolbarLayout === 'vertical' ? rect.right + 8 : rect.left;
        const left = popupHorizontalAnchor(idealLeft, symbolsPopupWidthPx);
        if (toolbarLayout === 'vertical') {
          // Landscape only: this popup has by far the most content of the 3, and the Symbols
          // trigger sits partway down the toolbar (after the whole format-row grid) — staying
          // strictly adjacent to it (popupVerticalAnchor's usual gap-based approach) caps the
          // available height at whichever side is bigger, which here is only ~210px regardless
          // of the ~390px screen, confirmed live ("I just see three rows"). Decoupling from the
          // trigger's own position and using nearly the full viewport height instead is what the
          // user asked for directly — it does mean this popup can cover the trigger row itself,
          // an accepted trade-off here (Escape/click-outside still close it) matching the earlier
          // "let it overlay everything" ask for the portrait case.
          const margin = 8;
          setSymbolsPopupPos({ top: margin, maxHeightPx: window.innerHeight - margin * 2, left });
        } else {
          setSymbolsPopupPos({ ...popupVerticalAnchor(rect, POPUP_DESIRED_MAX_HEIGHT), left });
        }
      }
    }
    // Desktop/tablet only here — see the dedicated phone-context effect below for why phone can't
    // safely focus from this same effect (portal-vs-state-timing race, same as the color popup).
    if (!isPhoneContext) {
      requestAnimationFrame(() =>
        symbolsWrapperRef.current?.querySelector<HTMLButtonElement>('[data-sym-btn]')?.focus()
      );
    }
    function onClickOutside(e: MouseEvent) {
      if (symbolsWrapperRef.current && !symbolsWrapperRef.current.contains(e.target as Node) &&
          symbolsPopupPanelRef.current && !symbolsPopupPanelRef.current.contains(e.target as Node)) {
        setSymbolsPopup(false);
      }
    }
    // Document-level Escape backstop — see the color popup's identical addition above.
    function onKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        setSymbolsPopup(false);
        requestAnimationFrame(() => symbolsWrapperRef.current?.querySelector<HTMLButtonElement>('button')?.focus());
      }
    }
    document.addEventListener('mousedown', onClickOutside);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onClickOutside);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [symbolsPopup, toolbarLayout, symbolsPopupWidthPx, isPhoneContext]);

  // Phone-context focus-on-open — see the color popup's identical effect above for why this can't
  // just live inline in the effect above (the portal's own position state, set there, hasn't
  // landed yet within that same synchronous call).
  useEffect(() => {
    if (!symbolsPopup || !isPhoneContext || !symbolsPopupPos) return;
    requestAnimationFrame(() =>
      symbolsPopupPanelRef.current?.querySelector<HTMLButtonElement>('[data-sym-btn]')?.focus()
    );
  }, [symbolsPopup, isPhoneContext, symbolsPopupPos]);

  if (!editor) return null;

  // When targetEditor is set (e.g. MCQ shared toolbar), all commands target it instead of the local editor.
  const activeEd = targetEditor ?? editor;

  /**
   * ONE PICKER PER EDITOR INSTANCE, including the toolbar-less ones.
   *
   * The `{` shortcut lives in `editorProps`, so it fires on whichever editor holds the caret —
   * and the field it was built for, a block's question, is precisely the one that renders no
   * toolbar. Rendering the picker only beside the toolbar left that handle null, so the brace
   * was swallowed and no menu arrived: the shortcut was dead everywhere it mattered, and alive
   * only in the one place that already had a button for it.
   *
   * Reaching across to the shared toolbar's picker instead would be the wrong repair. A headless
   * editor is not always driven by one (the question editor has no toolbar at all), and the
   * insert has to land in THIS editor, which is what `activeEd` already resolves per instance.
   */
  const renderVariablePicker = (trigger: Parameters<typeof VariablePickerMenu>[0]['renderTrigger']) => (
    <VariablePickerMenu
      variables={variables.filter(v => v.name)}
      onChoose={(name) => {
        const picked = variables.find(v => v.name === name);
        if (!picked) return;
        activeEd.chain().focus().insertContent({ type: 'variableChip', attrs: { name, varId: picked.id } }).run();
      }}
      ariaLabel={t('blocks.toolbar.variablesPicker')}
      handleRef={variablePickerRef}
      /* Dismissed without choosing: put back the brace the shortcut swallowed, so a
         teacher who wanted a literal one gets it by pressing Escape. */
      onDismiss={() => activeEd?.chain().focus().insertContent('{').run()}
      renderTrigger={trigger}
    />
  );

  /**
   * THE EARLIER-ANSWER PICKER, rendered only where there is an experience to look into.
   *
   * `useAnswerRefScope` is null outside the task editor — in Preview, and in any field mounted
   * without the provider — and the button simply does not appear there. That is the honest
   * state: with no task list there is nothing to point at, and a button that opens an empty
   * menu is worse than no button.
   */
  const renderAnswerRefPicker = (trigger: Parameters<typeof AnswerRefPickerMenu>[0]['renderTrigger']) =>
    (answerRefScope ? (
      <AnswerRefPickerMenu
        tasks={answerRefScope.tasks}
        currentTaskId={answerRefScope.currentTaskId}
        onChoose={(ref) => {
          /*
           * The ALIAS for this address, found or created — never the address itself.
           *
           * This is where "put that reference in again and you get the alias" happens: a
           * second chip at an address the teacher has already named joins that alias rather
           * than starting a rival name for the same answer.
           */
          const aliasId = answerRefScope.ensureAlias(ref);
          const alias = answerRefScope.aliases.find(a => a.id === aliasId);
          activeEd.chain().focus().insertContent({
            type: 'answerRefChip',
            attrs: {
              aliasId,
              /*
               * FROM THE ADDRESS WHEN THE ALIAS IS NEW, which is most of the time.
               *
               * `ensureAlias` returns the id in the same tick but CREATES through state, so
               * the array closed over by this render does not hold the new one yet — the
               * lookup missed, and `aliasDisplayName(undefined)` returns "Missing block",
               * which is a non-empty string, so the `||` written for exactly this case never
               * ran. Every chip inserted since baked "Missing block" as its stored text.
               *
               * It never showed in the editor, where the live alias is drawn over it. It
               * showed everywhere the stored html is rendered raw — and in the accessible
               * name of the card holding it, which read "Edit text block: Missing block
               * Missing block Missing block".
               *
               * The ref is what is always in hand, so the address comes from that.
               */
              fallback: alias
                ? aliasChipName(alias, answerRefScope, t)
                : answerRefAddress(answerRefScope, ref, t),
            },
          }).run();
        }}
        ariaLabel={t('blocks.toolbar.answerRef')}
        handleRef={answerRefPickerRef}
        /* Dismissed without choosing: put back the bracket the shortcut swallowed, exactly as
           the variable picker above puts back its brace. A shortcut that eats a character a
           teacher meant literally is a shortcut they stop trusting. */
        onDismiss={() => activeEd?.chain().focus().insertContent('[').run()}
        renderTrigger={trigger}
      />
    ) : null);

  // Headless mode — contenteditable only, no toolbar. Modals still rendered for click-to-edit.
  if (hideToolbar) {
    return (
      <>
        {/* `data-field-id` goes on BOTH roots. This toolbar-less branch is the one every block's
            question editor takes (`BlockQuestionEditor` passes `hideToolbar`), so tagging only the
            branch below left the single most-edited field in the product untagged while looking,
            at the call site, exactly as though it were covered. */}
        {/* GROWS ONLY WHEN ASKED TO. `flex-1` was hardcoded here, so every block's question editor
            — which takes this branch and does NOT pass `fill` — absorbed all the leftover height
            of its panel: the bordered box sat at the top of a tall wrapper with dead space under
            it, read as a gap between the question and Answer Setup, and was blamed on the section
            heading. `fill`'s own doc already draws this line: "most callers use this for a
            question, where sizing to content is right". `text-block` is the one caller that passes
            both `fill` and `hideToolbar`, which is why the growth was wired in unconditionally;
            it keeps it, including the `md` variant. */}
        <div data-field-id={fieldId} className={`relative flex flex-col ${
          fill === 'md' ? 'md:min-h-0 md:flex-1' : fill ? 'min-h-0 flex-1' : ''}`}>
          {/* z-[1], deliberately: the placeholder DOES need to sit above EditorContent (being
              absolutely positioned isn't enough on its own — verified live, it disappears behind
              the tiptap paragraph without a z-index), but its old z-10 tied it with the sticky
              editor toolbar above, and since this paragraph comes LATER in the DOM the tie broke
              in its favour, so the ghost text painted straight over the toolbar while scrolling.
              Reported on GPS, but this is the shared component, so every block had it. Any
              positive value clears the content; staying below the toolbar's 10 is the point.

              `right-3` is load-bearing, not symmetry with `left-3`: an absolutely positioned box
              with only a left edge shrink-wraps to its content and extends rightward forever, so
              the ghost text could never wrap and simply ran out past the field's right border.
              Invisible on the short placeholders ("Enter your question") and plain on the long
              one -- FITB's, which has to describe two steps. Giving it both edges is what lets it
              wrap inside the box. */}
          {editor.isEmpty && placeholder && (
            <p aria-hidden='true' className='pointer-events-none absolute left-3 right-3 top-3 z-[1] select-none text-sm italic text-ws-muted'>
              {placeholder}
            </p>
          )}
          <EditorContent editor={editor} className={`flex min-h-0 flex-1 flex-col ${tiptapFill}`} />
        </div>

        {reactionsModal && (
          <Suspense fallback={null}>
            <ReactionsEditorModal
              initialFormula={reactionsInitialFormula}
              initialNote={reactionsInitialNote}
              initialFeynman={reactionsInitialFeynman}
              documentFormulas={documentFormulasOf(activeEd)}
              isUpdate={!!reactionsUpdateRef.current}
              onInsert={(formula, note, feynman) => {
                if (reactionsUpdateRef.current) {
                  reactionsUpdateRef.current({ formula, note, feynman });
                } else {
                  activeEd.chain().focus().insertContent({ type: 'reactionFormula', attrs: { formula, note, feynman } }).run();
                }
                reactionsUpdateRef.current = null;
                setReactionsModal(false);
              }}
              onClose={() => { setReactionsModal(false); reactionsUpdateRef.current = null; }}
            />
          </Suspense>
        )}

        {mathModal && (
          <Suspense fallback={null}>
            <MathEditorModal
              initialLatex={mathInitialLatex}
              isUpdate={!!mathUpdateRef.current}
              documentLatex={documentLatexOf(activeEd)}
              onInsert={(latex) => {
                if (mathUpdateRef.current) {
                  mathUpdateRef.current(latex);
                } else {
                  activeEd.chain().focus().insertContent({ type: 'mathFormula', attrs: { latex } }).run();
                }
                mathUpdateRef.current = null;
                setMathModal(false);
              }}
              onClose={() => { setMathModal(false); mathUpdateRef.current = null; }}
            />
          </Suspense>
        )}

        {/*
          * THE PICKERS ARE MOUNTED HERE TOO, invisibly, and the keyboard shortcuts depend on it.
          *
          * A block with a shared styling bar runs TWO editors: this one holds the content and
          * `hideToolbar`, and `AnchoredStylingBar` runs a second with `hideContent` that draws
          * the buttons. The buttons' pickers live on that one. The keystroke arrives HERE, so
          * `{` and `[` were reaching for a handle that is only ever set on the other instance
          * and silently doing nothing in every Text block and every question editor.
          *
          * The triggers are `sr-only` rather than absent: a caret-summoned menu positions
          * itself with `openAt`, but the component still reads its trigger to tell its own
          * clicks from outside ones, and a null ref reads as "hidden" and shuts the menu the
          * instant it opens.
          */}
        {/*
          * OUT OF FLOW, because they have no visual presence and the column they land in puts
          * `gap-4` between every child.
          *
          * `FlowPickerMenu` draws its own root — `relative flex items-center` — around whatever
          * trigger it is given. An `sr-only` trigger makes the BUTTON invisible and leaves that
          * root a zero-height flex item, so these two were spending 32px of gap between the
          * question and Answer Setup while showing nothing. Measured in the browser at 455 →
          * 503 where one 16px gap was due.
          *
          * This is the second cause of that space. The first was `flex-1` hardcoded on the
          * editor root (2a52ff63); fixing it left this one as the visible remainder, which is
          * why the gap looked like it had come back.
          *
          * Safe to lift out because the menus are PORTALLED and `fixed` — where this box sits
          * has no bearing on where they open — and because both triggers are `tabIndex={-1}`
          * stand-ins for the real buttons on the styling bar, existing only so the caret
          * shortcut has a ref to read.
          */}
        <div className='absolute'>
          {varAffordance !== 'absent' && renderVariablePicker((p) => (
            <button ref={p.ref} type='button' aria-haspopup={p['aria-haspopup']}
              /* Inert for real, not just by label. This stand-in is sr-only and `tabIndex={-1}`,
                 so no pointer and no Tab reaches it — but a screen reader in browse mode does,
                 and `aria-disabled` alone would have opened the menu for the one person who
                 could still press it. */
              onClick={varAffordance === 'disabled' ? undefined : p.onClick}
              aria-expanded={p['aria-expanded']} aria-disabled={varAffordance === 'disabled' || undefined}
              className='sr-only' tabIndex={-1}>
              {t(varAffordance === 'disabled' ? 'blocks.toolbar.variablesLocked' : 'blocks.toolbar.variables')}
            </button>
          ))}
          {refAffordance !== 'absent' && renderAnswerRefPicker((p) => (
            <button ref={p.ref} type='button' aria-haspopup={p['aria-haspopup']}
              /* Inert for real, not just by label — see the variables stand-in above: this is
                 sr-only and `tabIndex={-1}`, so only a screen reader reaches it, and
                 `aria-disabled` alone would open the menu for exactly that person. */
              onClick={refAffordance === 'disabled' ? undefined : p.onClick}
              aria-disabled={refAffordance === 'disabled' || undefined}
              aria-expanded={p['aria-expanded']} className='sr-only' tabIndex={-1}>
              {t(refAffordance === 'disabled'
                ? 'blocks.toolbar.answerRefLocked' : 'blocks.toolbar.answerRef')}
            </button>
          ))}
        </div>

      </>
    );
  }

  const alignIdx = activeEd.isActive({ textAlign: 'center' })  ? 1
                 : activeEd.isActive({ textAlign: 'right' })   ? 2
                 : activeEd.isActive({ textAlign: 'justify' }) ? 3
                 : 0;
  const { Icon: AlignIcon } = getAlignEntry(alignIdx);
  const { labelKey: nextAlignLabelKey } = getAlignEntry((alignIdx + 1) % 4);

  function applyColor(color: string | null) {
    setColorPopup(false);
    if (color === null) {
      activeEd.chain().focus().unsetColor().run();
      setColorLiveMsg('');
      requestAnimationFrame(() => setColorLiveMsg(t('blocks.toolbar.colorDefault')));
    } else {
      setLastColor(color);
      activeEd.chain().focus().setColor(color).run();
      const label = t((INK_COLORS.find(c => c.value === color)?.labelKey ?? '') as string);
      setColorLiveMsg('');
      requestAnimationFrame(() => setColorLiveMsg(label));
    }
  }

  return (
    <div data-field-id={fieldId} className={`flex flex-col gap-2 ${fill === 'md' ? 'md:min-h-0 md:flex-1' : fill ? 'min-h-0 flex-1' : ''}`}>
      {/* Toolbar — reserves its full height at all times rather than collapsing (display:none)
          when hidden, so a shared toolbar anchored above a scrollable list never causes the
          list to jump as focus moves between stylable and non-stylable fields; only its
          opacity/interactivity toggles. Ghosted (dimmed), not fully invisible, when hidden — an
          opacity-0 box read as unexplained blank whitespace; a faint outline signals "a toolbar
          lives here, focus a stylable field to use it" instead. */}
      <div
        {...(toolbarVisible === false ? { inert: '' } : {})}
        /*
         * TWO COLUMNS IN THE RAIL, not two stacked groups.
         *
         * The rail is 96px of width and, on the shortest phones in landscape, less height than
         * it needed: measured at 667x375, the last insert button's bottom edge fell at 388
         * against a 375 viewport, so `{}` sat below the fold of a scroller that does not look
         * like one. The insert buttons were the expensive part — full rail width, one per row,
         * for glyphs that need about 20px.
         *
         * Splitting the two families into two COLUMNS costs nothing and buys a row: formatting
         * down the left, alignment and the inserts down the right. The family boundary becomes
         * the gutter, which is a stronger divider than the rule it replaces. Measured after:
         * last bottom 301 with 74px of headroom, and the buttons grew from 31px to 39px.
         */
        className={`relative flex transition-opacity duration-150 ${
          toolbarLayout === 'vertical' ? 'items-start gap-1.5' : 'flex-col gap-1'
        } ${toolbarVisible === false ? 'pointer-events-none opacity-30' : 'opacity-100'}`}
      >

        {/* Format row: Bold / Italic / Underline / Sub / Super / Color / Align. A 2-column grid
            in vertical mode (not a single flex-col stack) — roughly halves the rail's required
            height, reducing (often eliminating, at typical phone landscape heights) the need to
            scroll a stack of onMouseDown-firing buttons, where a touch-scroll swipe starting on a
            button would otherwise fire it before the browser can tell a tap from a drag. */}
        <div className={toolbarLayout === 'vertical' ? 'grid flex-1 grid-cols-1 gap-1.5' : 'grid grid-cols-6 gap-1.5'}>
          <ToolbarButton
            onClick={() => activeEd.chain().focus().toggleBold().run()}
            label={t('blocks.toolbar.bold')}
            isActive={activeEd.isActive('bold')}
          >
            <Bold className='h-5 w-5' strokeWidth={3} />
          </ToolbarButton>

          <ToolbarButton
            onClick={() => activeEd.chain().focus().toggleItalic().run()}
            label={t('blocks.toolbar.italic')}
            isActive={activeEd.isActive('italic')}
          >
            <Italic className='h-5 w-5' />
          </ToolbarButton>

          <ToolbarButton
            onClick={() => activeEd.chain().focus().toggleUnderline().run()}
            label={t('blocks.toolbar.underline')}
            isActive={activeEd.isActive('underline')}
          >
            <UnderlineIcon className='h-5 w-5' />
          </ToolbarButton>

          <ToolbarButton
            onClick={() => activeEd.chain().focus().unsetMark('superscript').toggleSubscript().run()}
            label={t('blocks.toolbar.subscript')}
            isActive={activeEd.isActive('subscript')}
          >
            <SubscriptIcon className='h-5 w-5' />
          </ToolbarButton>

          <ToolbarButton
            onClick={() => activeEd.chain().focus().unsetMark('subscript').toggleSuperscript().run()}
            label={t('blocks.toolbar.superscript')}
            isActive={activeEd.isActive('superscript')}
          >
            <SuperscriptIcon className='h-5 w-5' />
          </ToolbarButton>

          {/* Color picker */}
          <div
            ref={colorWrapperRef}
            // Also checks colorPopupPanelRef (null on desktop/tablet, a no-op there) — in a phone
            // context the panel is portaled to document.body, entirely outside this wrapper, so
            // moving focus INTO the popup itself (e.g. the autofocus-on-open above, or arrow-key
            // navigation between swatches) is itself a "blur went outside the wrapper" event.
            // Checking the wrapper alone closed the popup the instant focus successfully landed
            // inside it — confirmed live, and the reason the autofocus/arrow-key fixes above
            // appeared to do nothing until this was fixed too.
            onBlur={(e) => {
              if (!colorWrapperRef.current?.contains(e.relatedTarget as Node) &&
                  !colorPopupPanelRef.current?.contains(e.relatedTarget as Node)) {
                setColorPopup(false);
              }
            }}
          >
            <ToolbarButton
              onClick={() => setColorPopup(v => !v)}
              label={t('blocks.toolbar.textColor')}
              isExpanded={colorPopup}
            >
              <Droplet
                className='h-5 w-5'
                style={lastColor ? { color: lastColor, filter: 'drop-shadow(0 0 0.5px rgba(0,0,0,0.35))' } : undefined}
              />
            </ToolbarButton>
            {colorPopup && (() => {
              const dialog = (
                <div
                  ref={isPhoneContext ? colorPopupPanelRef : undefined}
                  role='dialog'
                  aria-label={t('blocks.toolbar.textColor')}
                  aria-modal='false'
                  // Portrait: full-width like desktop/tablet's own w-full — this popup is now
                  // portaled/fixed (no relative parent to size against), so "full width" means an
                  // explicit viewport-relative width instead. Landscape: keeps the fixed w-56,
                  // appropriately sized for the rail's own narrower context.
                  className={isPhonePortrait
                    ? 'w-[calc(100vw-16px)] overflow-y-auto rounded-xl border border-ws-border bg-ws-elevated p-2 shadow-lg'
                    : isPhoneContext
                    ? 'w-56 overflow-y-auto rounded-xl border border-ws-border bg-ws-elevated p-2 shadow-lg'
                    : 'absolute left-0 top-full z-50 mt-2 w-full rounded-xl border border-ws-border bg-ws-elevated p-2 shadow-lg'}
                  // maxHeight is the actual measured space on whichever side popupVerticalAnchor
                  // picked (this popup's own content is short enough to never actually hit it, but
                  // every popup uses the same computed value rather than a special case).
                  style={isPhoneContext && colorPopupPos
                    ? { position: 'fixed', ...('top' in colorPopupPos ? { top: colorPopupPos.top } : { bottom: colorPopupPos.bottom }), left: colorPopupPos.left, zIndex: 9999, maxHeight: colorPopupPos.maxHeightPx }
                    : undefined}
                  onKeyDown={(e) => {
                    if (e.key === 'Escape') {
                      e.stopPropagation();
                      setColorPopup(false);
                      requestAnimationFrame(() =>
                        colorWrapperRef.current?.querySelector<HTMLButtonElement>('button')?.focus()
                      );
                    }
                  }}
                >
                  {/* Shared grid, so this picker and the two formula pickers are one look. The
                      Default control goes INSIDE it, joining the same arrow-key sequence rather
                      than sitting outside it. */}
                  <InkSwatchGrid
                    onPick={(v) => applyColor(v)}
                    size={isPhoneContext ? 'lg' : 'md'}
                    clearLabel={t('blocks.toolbar.colorDefault')}
                    onClear={() => applyColor(null)}
                  />
                </div>
              );
              return isPhoneContext
                ? (colorPopupPos ? createPortal(dialog, document.body) : null)
                : dialog;
            })()}
          </div>

          {/* Alignment — cycles through left → center → right → justify. The 7th item in a
              2-column grid (vertical mode) would otherwise sit alone at the start of its own row,
              leaving an awkward empty cell beside it — centered instead via col-span-2, with an
              inner half-width wrapper so ToolbarButton's own w-full fills a single-button-sized
              box rather than the whole spanned row. Horizontal mode (grid-cols-7) is unaffected:
              this is just the button's own grid cell there. */}
        </div>

        {/* The rule between the families, horizontal only — in vertical the gutter does it. */}
        {toolbarLayout !== 'vertical' && <div className='border-t border-ws-border/40' />}

        {/* Insert row: Symbols / Math / Chemical / Variable — variables sit last since inserting
            one is the most advanced action here (it changes the block's evaluation behavior,
            unlike a plain symbol/equation/reaction which is just content), not a peer to the
            simpler inserts it used to sit next to. */}
        <div className={toolbarLayout === 'vertical' ? 'flex flex-1 flex-col gap-1.5' : 'grid grid-cols-6 gap-1.5'}>
          {/*
            * ALIGNMENT LEADS THIS GROUP, on every surface.
            *
            * The split is CHARACTER formatting against everything else, not formatting against
            * inserts. Alignment is a paragraph property and never belonged beside bold and
            * italic — it sat there because seven cells happened to fit. Grouping it here makes
            * the families honest, keeps the surfaces from diverging (a teacher who learns the
            * phone layout finds it in the same place on desktop), and lands both groups on six.
            */}
          <ToolbarButton
            onClick={() => activeEd.chain().focus().setTextAlign(ALIGNS[(alignIdx + 1) % 4]!).run()}
            label={t(nextAlignLabelKey)}
          >
            <AlignIcon className='h-5 w-5' />
          </ToolbarButton>
          {/* Symbols */}
          <div
            ref={symbolsWrapperRef}
            // Same wrapper-vs-portaled-panel fix as the color picker above.
            onBlur={(e) => {
              if (!symbolsWrapperRef.current?.contains(e.relatedTarget as Node) &&
                  !symbolsPopupPanelRef.current?.contains(e.relatedTarget as Node)) {
                setSymbolsPopup(false);
              }
            }}
          >
            <ToolbarButton
              onClick={() => setSymbolsPopup(v => !v)}
              label={t('blocks.toolbar.symbols')}
              isExpanded={symbolsPopup}
            >
              <span aria-hidden='true' className='select-none font-serif text-lg leading-none'>Ω</span>
            </ToolbarButton>
            {symbolsPopup && (() => {
              let gIdx = 0;
              const dialog = (
                <div
                  ref={isPhoneContext ? symbolsPopupPanelRef : undefined}
                  role='dialog'
                  aria-label={t('blocks.toolbar.symbols')}
                  aria-modal='false'
                  // Phone: wider than the other 2 popups' w-56 — this is a grid of individually
                  // tappable symbols, not a handful of large chips/swatches; too narrow a column
                  // crowds adjacent symbols enough to mis-tap. Landscape goes wider still
                  // (w-[780px], 17 columns via symbolsCols above) — trading its abundant width for
                  // far fewer rows, since its own viewport has almost no vertical room to spare.
                  // Must stay in sync with symbolsPopupWidthPx above (the clamp math needs the
                  // same number the CSS renders). Desktop/tablet: restored to the original plain
                  // in-place `absolute` popup — a portaled, independently-positioned popup there
                  // started overlapping unrelated neighboring toolbar buttons instead of just the
                  // row below it, confirmed live.
                  className={isPhoneContext
                    ? `overflow-y-auto rounded-xl border border-ws-border bg-ws-elevated p-3 shadow-lg ${toolbarLayout === 'vertical' ? 'w-[780px]' : 'w-96'}`
                    : 'absolute left-0 top-full z-50 mt-2 w-full overflow-y-auto rounded-xl border border-ws-border bg-ws-elevated p-3 shadow-lg'}
                  // Phone: maxHeight is the actual measured space on whichever side
                  // popupVerticalAnchor picked, capped at 700px — this needs ~650-770px of content
                  // depending on section count, so it automatically uses however much of that is
                  // genuinely free rather than a guessed buffer (confirmed live this matters: a
                  // static ceiling once let the popup's height exceed the real room above/below the
                  // trigger, silently overlapping it). Desktop/tablet keeps its own original
                  // (smaller, unrelated) 400px cap.
                  style={isPhoneContext
                    ? (symbolsPopupPos
                        ? { position: 'fixed', ...('top' in symbolsPopupPos ? { top: symbolsPopupPos.top } : { bottom: symbolsPopupPos.bottom }), left: symbolsPopupPos.left, zIndex: 9999, maxHeight: symbolsPopupPos.maxHeightPx }
                        : undefined)
                    : { maxHeight: 'min(400px, calc(100dvh - 120px))' }}
                  onKeyDown={(e) => {
                    if (e.key === 'Escape') {
                      e.stopPropagation();
                      setSymbolsPopup(false);
                      requestAnimationFrame(() =>
                        symbolsWrapperRef.current?.querySelector<HTMLButtonElement>('button')?.focus()
                      );
                    }
                  }}
                >
                  {SYMBOL_SECTIONS.map(({ key, items }) => (
                    <div key={key} className='mb-3 last:mb-0'>
                      <p id={`sym-section-${key}`} className='mb-1.5 text-[9px] font-semibold uppercase tracking-wider text-ws-muted'>
                        {t(`blocks.toolbar.symbolSection_${key}` as any)}
                      </p>
                      {/* Column count varies by toolbarLayout (14 landscape / 8 portrait), so
                          it can't be a static Tailwind grid-cols-N class — set via inline style
                          instead. */}
                      <div
                        role='group'
                        aria-labelledby={`sym-section-${key}`}
                        className='grid gap-1'
                        style={{ gridTemplateColumns: `repeat(${symbolsCols}, minmax(0, 1fr))` }}
                      >
                        {items.map(({ sym, labelKey, isCommon }) => {
                          const idx = gIdx++;
                          return (
                            <button
                              key={sym}
                              type='button'
                              data-sym-btn=''
                              tabIndex={idx === symbolFocusIdx ? 0 : -1}
                              aria-label={t(labelKey as any)}
                              title={t(labelKey as any)}
                              onMouseDown={(e) => e.preventDefault()}
                              onKeyDown={(e) => {
                                // Phone context portals this panel to document.body —
                                // symbolsWrapperRef only ever wraps the trigger button there, so
                                // it never contains these symbol buttons; symbolsPopupPanelRef
                                // (the portaled dialog) does. Same mismatch as the initial-open
                                // autofocus above — confirmed live it silently broke arrow-key
                                // navigation the same way (the "current" swatch/symbol updated via
                                // tabIndex, but real keyboard focus never actually moved off the
                                // first one).
                                const focusSymbolAt = (i: number) =>
                                  (isPhoneContext ? symbolsPopupPanelRef.current : symbolsWrapperRef.current)
                                    ?.querySelectorAll<HTMLButtonElement>('[data-sym-btn]')[i]?.focus();
                                if (e.key === 'ArrowRight') {
                                  e.preventDefault();
                                  const next = Math.min(idx + 1, TOTAL_SYMBOLS - 1);
                                  setSymbolFocusIdx(next);
                                  focusSymbolAt(next);
                                } else if (e.key === 'ArrowLeft') {
                                  e.preventDefault();
                                  const prev = Math.max(idx - 1, 0);
                                  setSymbolFocusIdx(prev);
                                  focusSymbolAt(prev);
                                } else if (e.key === 'ArrowDown') {
                                  e.preventDefault();
                                  const next = Math.min(idx + symbolsCols, TOTAL_SYMBOLS - 1);
                                  setSymbolFocusIdx(next);
                                  focusSymbolAt(next);
                                } else if (e.key === 'ArrowUp') {
                                  e.preventDefault();
                                  const prev = Math.max(idx - symbolsCols, 0);
                                  setSymbolFocusIdx(prev);
                                  focusSymbolAt(prev);
                                } else if (e.key === 'Home') {
                                  e.preventDefault();
                                  setSymbolFocusIdx(0);
                                  focusSymbolAt(0);
                                } else if (e.key === 'End') {
                                  e.preventDefault();
                                  setSymbolFocusIdx(TOTAL_SYMBOLS - 1);
                                  focusSymbolAt(TOTAL_SYMBOLS - 1);
                                }
                              }}
                              onClick={() => {
                                activeEd.chain().focus().insertContent(sym).run();
                                setSymbolsPopup(false);
                              }}
                              // isCommon replaces the old dedicated "Common" section (removed —
                              // it was a pure duplicate of entries already here) — a tinted
                              // border/background instead of a separate row/header, so these
                              // stay quick to spot without costing any vertical space.
                              //
                              // Bigger buttons (h-10) in ANY phone context, not just the landscape
                              // rail — confirmed live portrait's own 32px buttons are still under
                              // typical touch-target guidelines, just less painfully so than
                              // landscape's original cramped case (portrait's wider w-96 popup
                              // gives real gap around each button, so mis-taps were less likely,
                              // but the buttons themselves were never actually bigger). Desktop/
                              // tablet keeps the original 32px size (mouse-driven, no need).
                              className={`flex items-center justify-center rounded border font-serif hover:bg-ws-hover ${
                                // dark:border/bg/text-ws-text-link — ws-primary is a dark, low-luminance
                                // blue in dark mode, nearly unreadable at this size/weight; same fix
                                // already used for this exact contrast issue elsewhere (see
                                // variable-chip-extension.tsx, feedback-card.tsx, formula-field.tsx).
                                // Dark mode additionally needs a stronger border (full opacity, not
                                // /50) and background tint (/25, not /10): ws-text-link's own
                                // lightness is close enough to the default text color (ws-text) that
                                // the light-mode opacities left isCommon barely distinguishable from
                                // a plain symbol button once ws-primary's near-white glyph color
                                // stopped being the differentiator.
                                isCommon ? 'border-ws-primary/50 dark:border-ws-text-link bg-ws-primary/10 dark:bg-ws-text-link/25 text-ws-primary dark:text-ws-text-link' : 'border-ws-border bg-ws-btn text-ws-text'
                              } ${isPhoneContext ? 'h-10 w-10 text-base' : 'h-8 w-8 text-sm'}`}
                            >
                              {sym}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>
              );
              return isPhoneContext
                ? (symbolsPopupPos ? createPortal(dialog, document.body) : null)
                : dialog;
            })()}
          </div>

          {/* Math formula */}
          <ToolbarButton
            onClick={() => { setMathInitialLatex(''); mathUpdateRef.current = null; setMathModal(true); }}
            label={t('blocks.toolbar.mathFormula')}
            isExpanded={mathModal}
          >
            <span aria-hidden='true' className='select-none font-serif text-sm italic leading-none'>f(x)</span>
          </ToolbarButton>

          {/* Chemical formula */}
          <ToolbarButton
            onClick={() => { reactionsUpdateRef.current = null; setReactionsInitialFormula(''); setReactionsModal(true); }}
            label={t('blocks.toolbar.chemFormula')}
            isExpanded={reactionsModal}
          >
            <FlaskConical className='h-5 w-5' />
          </ToolbarButton>

          {/* GROUPED AND SEARCHABLE, the same control the formula field uses.
              A flat wrap of every chip in the task stopped being scannable the moment sets made
              twenty-five variables ordinary — five stars with five properties each. */}
          {varAffordance !== 'absent' && renderVariablePicker((p) => (
            <ToolbarButton
              ref={p.ref}
              onClick={p.onClick}
              aria-haspopup={p['aria-haspopup']}
              /* The NAME carries the reason, because this app puts no explanatory note in the
                 layout — and "not yet" is the half that teaches the rung. */
              label={t(varAffordance === 'disabled' ? 'blocks.toolbar.variablesLocked' : 'blocks.toolbar.variables')}
              isDisabled={varAffordance === 'disabled'}
              isExpanded={p['aria-expanded']}
            >
              <Braces className='h-5 w-5' />
            </ToolbarButton>
          ))}

          {/*
            * EARLIER ANSWERS, last — the most advanced insert here, and the only one that
            * reaches outside this block.
            *
            * `[ ]`, after a spell on `[[ ]]`. The doubled form was chosen because single
            * brackets are live notation in the subject matter — `[H+]` is a concentration,
            * `[0, 10]` an interval, `[1]` a citation — and it is still true that a chip
            * dropped into a plain field as `[name]` could be read as one of those. The owner
            * weighed that against how the doubled form looked on a button and in a sentence
            * and chose the single. The collision is survivable because the brackets are not
            * load-bearing: a chip is identified by its attributes, never by its text.
            */}
          {refAffordance !== 'absent' && renderAnswerRefPicker((p) => (
            <ToolbarButton
              ref={p.ref}
              onClick={p.onClick}
              aria-haspopup={p['aria-haspopup']}
              /* The NAME carries the reason, because this app puts no explanatory note in the
                 layout — and "not yet" is the half that teaches the rung. */
              label={t(refAffordance === 'disabled'
                ? 'blocks.toolbar.answerRefLocked' : 'blocks.toolbar.answerRef')}
              isDisabled={refAffordance === 'disabled'}
              isExpanded={p['aria-expanded']}
            >
              <Brackets className='h-5 w-5' />
            </ToolbarButton>
          ))}
        </div>
      </div>

      {/* Editor */}
      {!hideContent && (
        // `min-h-0` on both this and EditorContent below: a flex item's default `min-height: auto`
        // refuses to shrink below its content, so a `flex-1 overflow-y-auto` ProseMirror inside grew
        // to its full text height instead of scrolling, and the clip above it put the last lines out
        // of reach. Harmless when the parent is auto-height — there is nothing to shrink against.
        <div className='relative flex min-h-0 flex-1 flex-col'>
          {/* z-[1], deliberately: the placeholder DOES need to sit above EditorContent (being
              absolutely positioned isn't enough on its own — verified live, it disappears behind
              the tiptap paragraph without a z-index), but its old z-10 tied it with the sticky
              editor toolbar above, and since this paragraph comes LATER in the DOM the tie broke
              in its favour, so the ghost text painted straight over the toolbar while scrolling.
              Reported on GPS, but this is the shared component, so every block had it. Any
              positive value clears the content; staying below the toolbar's 10 is the point. */}
          {editor.isEmpty && placeholder && (
            <p aria-hidden='true' className='pointer-events-none absolute left-3 right-3 top-3 z-[1] select-none text-sm italic text-ws-muted'>
              {placeholder}
            </p>
          )}
          <EditorContent editor={editor} className={`flex min-h-0 flex-1 flex-col ${tiptapFill}`} />
        </div>
      )}

      {/* Announces color changes to screen readers */}
      <span role='status' aria-live='polite' aria-atomic='true' className='sr-only'>{colorLiveMsg}</span>

      {/* Reactions editor modal — lazy loaded */}
      {reactionsModal && (
        <Suspense fallback={null}>
          <ReactionsEditorModal
            initialFormula={reactionsInitialFormula}
            initialNote={reactionsInitialNote}
            initialFeynman={reactionsInitialFeynman}
            documentFormulas={documentFormulasOf(activeEd)}
            isUpdate={!!reactionsUpdateRef.current}
            onInsert={(formula, note, feynman) => {
              if (reactionsUpdateRef.current) {
                reactionsUpdateRef.current({ formula, note, feynman });
              } else {
                activeEd.chain().focus().insertContent({ type: 'reactionFormula', attrs: { formula, note, feynman } }).run();
              }
              reactionsUpdateRef.current = null;
            }}
            onClose={() => { setReactionsModal(false); reactionsUpdateRef.current = null; }}
          />
        </Suspense>
      )}

      {/* Math editor modal — lazy loaded */}
      {mathModal && (
        <Suspense fallback={null}>
          <MathEditorModal
            initialLatex={mathInitialLatex}
            isUpdate={!!mathUpdateRef.current}
            documentLatex={documentLatexOf(activeEd)}
            onInsert={(latex) => {
              if (mathUpdateRef.current) {
                mathUpdateRef.current(latex);
              } else {
                activeEd.chain().focus().insertContent({ type: 'mathFormula', attrs: { latex } }).run();
              }
              mathUpdateRef.current = null;
            }}
            onClose={() => { setMathModal(false); mathUpdateRef.current = null; }}
          />
        </Suspense>
      )}
    </div>
  );
}
